import { useEffect, useState } from "react";
import { Link2Off, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/components/Bits";

export const EmailDialog = ({ open, onOpenChange, folder, onSent }) => {
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const title = folder ? folder.name : "My Roots Playbook";
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/playbook/email", { folder_id: folder?.id || null, message });
      toast.success(`Sent to ${data.sent_to}. Forward it to whoever needs it.`);
      setMessage(""); onOpenChange(false); onSent();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="playbook-email-dialog">
        <DialogHeader>
          <DialogTitle>Email me "{title}"</DialogTitle>
          <DialogDescription>We'll email you a link to the branded PDF so you can forward it to your business partner or team. The link always gives the latest version.</DialogDescription>
        </DialogHeader>
        <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={4} maxLength={500} data-testid="playbook-email-message"
          placeholder="Add a short message for whoever you forward it to (optional), e.g. 'Sam, here's the Christmas plan we talked about.'" />
        <p className="text-xs text-muted-foreground text-right">{message.length}/500</p>
        <Button onClick={send} disabled={busy} data-testid="playbook-email-send" className="rounded-full bg-navy hover:bg-navy/90 text-white">
          {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}Send to my email
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const SharedLinks = ({ reloadKey }) => {
  const [shares, setShares] = useState([]);
  useEffect(() => { api.get("/playbook/shares").then((r) => setShares(r.data)).catch(() => {}); }, [reloadKey]);
  const revoke = async (id) => {
    if (!window.confirm("Switch this link off? Anyone you've forwarded it to won't be able to download it any more.")) return;
    try { await api.delete(`/playbook/shares/${id}`); setShares((l) => l.filter((s) => s.id !== id)); toast.success("Link switched off"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  if (!shares.length) return null;
  return (
    <details className="mt-12 rounded-2xl border border-border bg-card p-5" data-testid="playbook-shared-links">
      <summary className="cursor-pointer text-sm font-medium">Links you've emailed ({shares.length})</summary>
      <ul className="mt-4 space-y-2">{shares.map((s) => (
        <li key={s.id} className="flex items-center justify-between gap-3 text-sm" data-testid={`playbook-share-${s.id}`}>
          <span>{s.title} <span className="text-xs text-muted-foreground">· {fmtDate(s.created_at)}</span></span>
          <button onClick={() => revoke(s.id)} data-testid={`playbook-share-revoke-${s.id}`} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-rose-600"><Link2Off className="h-3.5 w-3.5" />Switch off</button>
        </li>
      ))}</ul>
    </details>
  );
};
